import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeftIcon, ChevronUpDownIcon, CheckIcon } from '@heroicons/react/24/outline';
import { Combobox } from '@headlessui/react';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import { useDynamicPermissions } from '../../hooks/useDynamicPermissions';
import RichTextEditor from '../../components/RichTextEditor';

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: {
    name: string;
  } | null;
}

interface FormData {
  name: string;
  description: string;
  ownerId: string;
  active: boolean;
}

const KnowledgeBaseForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    ownerId: '',
    active: true,
  });
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [ownerQuery, setOwnerQuery] = useState('');
  const { can, loading: permissionsLoading } = useDynamicPermissions();

  const canCreate = can('knowledge_base', 'create');
  const canUpdate = can('knowledge_base', 'update');

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch users for owner dropdown - only managers and admins
        const usersResponse = await api.get('/users?limit=500');
        const allUsers = usersResponse.data.users || [];
        const managerAdminRoles = ['manager', 'admin', 'super_admin'];
        const filteredUsers = allUsers.filter((user: User) =>
          user.role?.name && managerAdminRoles.includes(user.role.name)
        );
        setUsers(filteredUsers);

        // If editing, fetch existing knowledge base
        if (isEditMode && id) {
          const kbResponse = await api.get(`/knowledge-base/${id}`);
          const kb = kbResponse.data;
          setFormData({
            name: kb.name || '',
            description: kb.description || '',
            ownerId: kb.ownerId || '',
            active: kb.active ?? true,
          });
        }
      } catch (err: any) {
        toast.error(err.response?.data?.error || 'Failed to load data');
        navigate('/knowledge-base');
      } finally {
        setInitialLoading(false);
      }
    };

    if (!permissionsLoading) {
      fetchData();
    }
  }, [id, isEditMode, navigate, permissionsLoading]);

  const selectedOwner = useMemo(() => {
    return users.find((user) => user.id === formData.ownerId) || null;
  }, [users, formData.ownerId]);

  const filteredUsers = useMemo(() => {
    if (!ownerQuery) return users;
    const query = ownerQuery.toLowerCase();
    return users.filter((user) => {
      const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
      return fullName.includes(query) || user.email.toLowerCase().includes(query);
    });
  }, [users, ownerQuery]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Title is required';
    }

    if (!formData.ownerId) {
      newErrors.ownerId = 'Owner is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        ownerId: formData.ownerId || null,
        active: formData.active,
      };

      if (isEditMode) {
        await api.put(`/knowledge-base/${id}`, payload);
        toast.success('Knowledge base updated successfully');
      } else {
        await api.post('/knowledge-base', payload);
        toast.success('Knowledge base created successfully');
      }

      navigate('/knowledge-base');
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to save knowledge base';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const newValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    // Clear error when field is modified
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleOwnerChange = (user: User | null) => {
    setFormData((prev) => ({
      ...prev,
      ownerId: user?.id || '',
    }));

    // Clear error when field is modified
    if (errors.ownerId) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.ownerId;
        return newErrors;
      });
    }
  };

  if (permissionsLoading || initialLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if ((isEditMode && !canUpdate) || (!isEditMode && !canCreate)) {
    return (
      <div className="p-8">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">
            You don't have permission to {isEditMode ? 'edit' : 'create'} knowledge bases.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-6">
        <Link
          to="/knowledge-base"
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Knowledge Base
        </Link>
      </div>

      <div className="max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">
          {isEditMode ? 'Edit Knowledge Base' : 'Create Knowledge Base'}
        </h1>

        <form onSubmit={handleSubmit} className="bg-white shadow rounded-lg p-6 space-y-6">
          {/* Title */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`mt-1 block w-full rounded-md border py-2 pl-3 pr-3 shadow-sm sm:text-sm ${
                errors.name
                  ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                  : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
              }`}
              placeholder="Enter knowledge base title"
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <RichTextEditor
              value={formData.description}
              onChange={(value) => setFormData((prev) => ({ ...prev, description: value }))}
              placeholder="Enter description (optional)"
              minHeight="120px"
            />
          </div>

          {/* Owner - Headless UI Combobox */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Owner <span className="text-red-500">*</span>
            </label>
            <Combobox value={selectedOwner} onChange={handleOwnerChange}>
              <div className="relative mt-1">
                <div className="relative w-full">
                  <Combobox.Input
                    className={`w-full rounded-md border py-2 pl-3 pr-10 shadow-sm sm:text-sm ${
                      errors.ownerId
                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                        : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                    }`}
                    displayValue={(user: User | null) =>
                      user ? `${user.firstName} ${user.lastName} (${user.email})` : ''
                    }
                    onChange={(event) => setOwnerQuery(event.target.value)}
                    placeholder="Search for an owner..."
                  />
                  <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                  </Combobox.Button>
                </div>
                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                  {filteredUsers.length === 0 && ownerQuery !== '' ? (
                    <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                      No users found.
                    </div>
                  ) : (
                    filteredUsers.map((user) => (
                      <Combobox.Option
                        key={user.id}
                        className={({ active }) =>
                          `relative cursor-default select-none py-2 pl-10 pr-4 ${
                            active ? 'bg-blue-600 text-white' : 'text-gray-900'
                          }`
                        }
                        value={user}
                      >
                        {({ selected, active }) => (
                          <>
                            <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                              {user.firstName} {user.lastName} ({user.email})
                            </span>
                            {selected ? (
                              <span
                                className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                  active ? 'text-white' : 'text-blue-600'
                                }`}
                              >
                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                              </span>
                            ) : null}
                          </>
                        )}
                      </Combobox.Option>
                    ))
                  )}
                </Combobox.Options>
              </div>
            </Combobox>
            {errors.ownerId && <p className="mt-1 text-sm text-red-600">{errors.ownerId}</p>}
          </div>

          {/* Active */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="active"
              name="active"
              checked={formData.active}
              onChange={handleChange}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="active" className="ml-2 block text-sm text-gray-700">
              Active
            </label>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Link
              to="/knowledge-base"
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default KnowledgeBaseForm;
